import React from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import ProfileAvatar from '../components/account/ProfileAvatar';
import LogoMark from '../components/LogoMark';
import LogoWordmark from '../components/LogoWordmark';
import Icon, { IconName } from '../components/ui/Icon';
import Text from '../components/ui/Text';
import { appVersion } from '../services/appVersion';
import { formatPhone } from '../services/auth';
import { membershipOnSale } from '../services/config';
import {
  colors,
  hairline,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../theme';

/**
 * The Account tab: who you are, what you have done, and everything you can
 * reach.
 *
 * Identity and a run of numbers on a dark header, then everything else as one
 * plain list.
 *
 * The referral and wallet cards that sat between them are gone: both are also
 * rows in the list, and a card plus a row for one destination is two of the
 * same thing competing.
 */

export type AccountAction =
  | 'edit-profile'
  | 'wallet'
  | 'settings'
  | 'bookings'
  | 'addresses'
  | 'payment'
  | 'ratings'
  | 'support'
  | 'refer'
  | 'subscription'
  | 'favourites'
  | 'history'
  | 'notifications'
  | 'privacy'
  | 'logout';

/**
 * Everything reachable from the account, in bands.
 *
 * One long list gave the same weight to changing your phone number and to
 * reading the privacy policy. Two bands separate what is yours - bookings,
 * addresses, cards, offers - from what belongs to the app, so the eye can skip
 * whichever half it did not come for.
 *
 * Editing your details is not a row here: the header itself is the control,
 * which is where someone looks for it anyway.
 *
 * Log out is a band of one, so it can never be hit by a near miss on the row
 * above it.
 */
const GROUPS: Array<Array<{ key: AccountAction; label: string; icon: IconName }>> = [
  /*
   * Homingo+ is a band of one, above everything else.
   *
   * It is the only row that sells something rather than opening something you
   * already have, and it loses that entirely as the seventh line of a list of
   * settings. Its own band is what gives it the weight without needing a
   * banner that would compete with the stats card above it.
   */
  [{ key: 'subscription', label: 'Homingo+ Membership', icon: 'star' }],
  [
    { key: 'bookings', label: 'My Bookings', icon: 'calendar' },
    { key: 'wallet', label: 'My Wallet', icon: 'wallet' },
    /* Directly under the wallet, which is where the reward lands. */
    { key: 'refer', label: 'Refer & Earn', icon: 'gift' },
    { key: 'addresses', label: 'Saved Addresses', icon: 'pin' },
    { key: 'payment', label: 'Payment Methods', icon: 'creditCard' },
    { key: 'ratings', label: 'My Ratings & Reviews', icon: 'star' },
  ],
  /*
   * Settings is still gone from this band on request - it only relayed to
   * pages already reachable from here. Privacy & Security is back: it now
   * carries the working Delete Account flow, which has to be reachable.
   */
  [
    { key: 'notifications', label: 'Notifications', icon: 'bell' },
    { key: 'privacy', label: 'Privacy & Security', icon: 'shieldCheck' },
    { key: 'support', label: 'Help & Support', icon: 'phone' },
  ],
];

/**
 * `GROUPS`, minus the Homingo+ band while the membership is not on sale.
 *
 * A function read at render, not a filtered constant: `/config` arrives after
 * this module is evaluated, and ops can put the membership on sale without a
 * new build.
 */
const visibleGroups = () =>
  membershipOnSale()
    ? GROUPS
    : GROUPS.filter(group => !group.some(item => item.key === 'subscription'));

/** One number on the strip under the header. */
export type Stat = {
  label: string;
  value: string;
  icon: IconName;
  /**
   * Where the number leads - the bookings tab, the ratings page. Optional so
   * a caller with nowhere to send a stat renders a plain figure rather than
   * a button that goes nowhere.
   */
  onPress?: () => void;
};

type Props = {
  signedIn: boolean;
  /** Null until a profile name has been set. */
  name: string | null;
  /** The verified number, digits only. Null when signed out. */
  phone: string | null;
  email?: string | null;
  /** Local URI of the profile picture. Falls back to the name's initial. */
  photo?: string | null;
  /** Counts for the strip under the header. */
  stats?: Stat[];
  onSelect: (action: AccountAction) => void;
  onSignIn: () => void;
};

/**
 * What the strip shows when the caller passes nothing.
 *
 * Zeroes, not a flattering set of invented numbers. This screen is rendered
 * with real counts by `HomeScreen`; the fallback exists for tests and for the
 * moment before the first fetch lands, and in both of those cases "32
 * bookings" is a claim about a person that nobody made. A new account really
 * has none, so zero is also the honest first-run answer.
 *
 * The rating is an em dash for the same reason it is in `HomeScreen`: nothing
 * rated is not the same as rated badly, and "0.0" says the second one.
 */
const DEFAULT_STATS: Stat[] = [
  { label: 'Bookings', value: '0', icon: 'calendar' },
  { label: 'Completed', value: '0', icon: 'checkCircle' },
  { label: 'Rating', value: '-', icon: 'star' },
  { label: 'Reviews', value: '0', icon: 'chat' },
];

function AccountScreen({
  signedIn,
  name,
  phone,
  email,
  photo,
  stats = DEFAULT_STATS,
  onSelect,
  onSignIn,
}: Props) {
  /*
   * Far under the tab bar's 64pt, deliberately.
   *
   * The footer is a version string and a copyright line - not something anyone
   * reads, and nothing is lost if the bar covers most of it once the list is
   * scrolled to its end. Padding enough to clear the bar left a band of empty
   * canvas below the last card, which read as the screen having failed to end.
   *
   * The trade is real, though: at this value the copyright is only fully
   * visible on a list long enough to overscroll. Anything that has to be read
   * belongs above the footer, not in it.
   */
  const bottomPad = spacing.xxl;

  /*
   * On a tablet the page becomes a centred column - including the dark header,
   * which then runs to both edges of that column rather than of the screen.
   * That is the intent: the band is the top of the page, and a page 560pt wide
   * with a header 1024pt wide is two different pages. Zero on a phone, where
   * the column is the screen.
   */
  const resp = useResponsive();

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={[{ paddingBottom: bottomPad }, resp.column]}
      showsVerticalScrollIndicator={false}
    >
      {/* ----------------------------------------------------------- header */}
      {/*
        Dark, and running to both edges. It is the only inverted surface in the
        app outside the cart bar, which is what makes the account feel like a
        different place rather than another page of the catalogue.
      */}
      <View style={styles.header}>
        <View style={styles.headerTop}>
          {/*
            Centred absolutely rather than laid out in the flow. It is the only
            thing on this strip now, so in the flow it would be centred anyway -
            but the header is a fixed band and the lockup is positioned against
            the band, not against whatever happens to share it.
          */}
          <View style={styles.brand} pointerEvents="none">
            {/* The mark holds its size; only the type grows, so the two do
                not scale together into a heavier lockup. */}
            <LogoMark size={20} color={colors.inkInverse} />
            <LogoWordmark height={22} color={colors.inkInverse} />
          </View>

        </View>

        <Pressable
          accessibilityRole="button"
          accessibilityLabel={signedIn ? 'Edit profile' : 'Sign in'}
          onPress={() => (signedIn ? onSelect('edit-profile') : onSignIn())}
          style={({ pressed }) => [styles.identity, pressed ? styles.pressed : null]}
        >
          {/* Photo, then the name's initial, then the mark - see
              `ProfileAvatar`. A signed-out visitor has no name, so they get the
              mark rather than a "G" for Guest, which would look like someone
              called G. */}
          <ProfileAvatar
            photo={signedIn ? photo : null}
            name={signedIn ? name : null}
            size={62}
          />

          <View style={styles.identityText}>
            <Text variant="titleLg" color={colors.inkInverse} numberOfLines={1}>
              {signedIn ? name ?? 'Add your name' : 'Guest'}
            </Text>

            {signedIn ? (
              <>
                <View style={styles.identityRow}>
                  <Icon
                    name="phone"
                    size={16}
                    color={colors.inkInverseMuted}
                    strokeWidth={1.9}
                  />
                  <Text variant="caption" color={colors.inkInverseMuted}>
                    {phone ? formatPhone(phone) : 'Number not set'}
                  </Text>
                </View>
                {email ? (
                  <View style={styles.identityRow}>
                    <Icon
                      name="share"
                      size={16}
                      color={colors.inkInverseMuted}
                      strokeWidth={1.9}
                    />
                    <Text variant="caption" color={colors.inkInverseMuted} numberOfLines={1}>
                      {email}
                    </Text>
                  </View>
                ) : null}
              </>
            ) : (
              <Text variant="caption" color={colors.inkInverseMuted}>
                Sign in to book and track services
              </Text>
            )}
          </View>

          <Icon name="chevronRight" size={18} color={colors.inkInverseMuted} />
        </Pressable>
      </View>

      {/* ------------------------------------------------------------ stats */}
      {/*
        Pulled up over the header's lower edge, so the two read as one block
        rather than as a dark strip with a card beneath it.
      */}
      <View style={styles.stats}>
        {stats.map((stat, i) => (
          <React.Fragment key={stat.label}>
            {i > 0 ? <View style={styles.statDivider} /> : null}
            {/* A number that leads somewhere is a button; one that does not
                stays a plain figure - same rule as every row below. */}
            <Pressable
              accessibilityRole={stat.onPress ? 'button' : undefined}
              accessibilityLabel={`${stat.value} ${stat.label}`}
              disabled={!stat.onPress}
              onPress={stat.onPress}
              style={({ pressed }) => [
                styles.stat,
                pressed && stat.onPress ? styles.pressed : null,
              ]}
            >
              <View style={styles.statIcon}>
                <Icon name={stat.icon} size={16} color={colors.primary} />
              </View>
              <Text variant="subtitle">{stat.value}</Text>
              <Text variant="caption" color={colors.inkMuted}>
                {stat.label}
              </Text>
            </Pressable>
          </React.Fragment>
        ))}
      </View>

      {/* ------------------------------------------------------------ list */}
      {visibleGroups().map((group, groupIndex) => (
        <View key={groupIndex} style={styles.group}>
          {group.map((item, i) => (
            <View key={item.key}>
              {i > 0 ? <View style={styles.separator} /> : null}
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={item.label}
                onPress={() => onSelect(item.key)}
                style={({ pressed }) => [styles.row, pressed ? styles.pressed : null]}
              >
                <View style={styles.rowIcon}>
                  <Icon
                    name={item.icon}
                    size={20}
                    color={colors.primary}
                    strokeWidth={1.9}
                  />
                </View>
                <Text variant="subtitle" style={styles.rowLabel}>
                  {item.label}
                </Text>
                <Icon name="chevronRight" size={16} color={colors.inkSubtle} />
              </Pressable>
            </View>
          ))}
        </View>
      ))}

      {/* Alone in its own card, so it cannot be hit by a near miss. */}
      {signedIn ? (
        <View style={styles.group}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Log out"
            onPress={() => onSelect('logout')}
            style={({ pressed }) => [styles.row, pressed ? styles.pressed : null]}
          >
            <View style={[styles.rowIcon, styles.rowIconDanger]}>
              <Icon
                name="logout"
                size={20}
                color={colors.danger}
                strokeWidth={1.9}
              />
            </View>
            <Text variant="subtitle" color={colors.danger} style={styles.rowLabel}>
              Log out
            </Text>
            <Icon name="chevronRight" size={16} color={colors.inkSubtle} />
          </Pressable>
        </View>
      ) : null}

      <View style={styles.footer}>
        <LogoMark size={16} color={colors.borderStrong} />
        {/*
          Read from the build, not typed here - the same fix Settings already
          carries, on the screen it was missed.

          This said "0.0.1" while the app in customers' hands was 2.0. It is
          the first thing support asks for, and a hand-typed one is worse than
          none: it makes a stale install indistinguishable from a current one,
          in exactly the conversation where that is the question.
        */}
        <Text variant="caption" color={colors.inkMuted} align="center">
          {`App version ${appVersion()}`}
        </Text>
        <Text variant="caption" color={colors.inkSubtle} align="center">
          © 2026 Homingo. All rights reserved.
        </Text>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
    backgroundColor: colors.background,
  },


  header: {
    paddingHorizontal: screenPadding,
    // Air above the lockup and the bell, which were sitting on the status bar.
    paddingTop: spacing.lg,
    // Room for the stats card, which is pulled up over this edge.
    paddingBottom: spacing.xxxl + spacing.xl,
    backgroundColor: colors.surfaceInverse,
    borderBottomLeftRadius: radii.xl,
    borderBottomRightRadius: radii.xl,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: 40,
    paddingBottom: spacing.lg,
  },
  /*
   * Lifted above the row rather than centred in it.
   *
   * A negative top raises the lockup past the row's own box; the header's top
   * padding is what it rises into. Kept after the bell was removed - it is
   * what sets the type off the status bar.
   */
  brand: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: -spacing.sm,
    bottom: spacing.md,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
  },
  identity: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.lg,
  },
  avatar: {
    width: 62,
    height: 62,
    borderRadius: 31,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
  },
  identityText: {
    flex: 1,
    gap: 3,
  },
  identityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },

  stats: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: screenPadding,
    // Overlaps the header's rounded lower edge.
    marginTop: -spacing.xxxl,
    paddingVertical: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  stat: {
    flex: 1,
    alignItems: 'center',
    gap: 2,
  },
  statIcon: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.xs,
    backgroundColor: colors.surfaceAccent,
  },
  /* A rule between the numbers, not around them - four bordered boxes would
     read as four buttons. */
  statDivider: {
    width: 1,
    height: 34,
    backgroundColor: colors.border,
  },

  group: {
    marginHorizontal: screenPadding,
    marginTop: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    overflow: 'hidden',
    ...hairline,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
  },
  /* A tinted bubble behind each glyph, which is what carries the colour now
     the tiles are gone - a bare icon in a row reads as a bullet. */
  rowIconDanger: {
    backgroundColor: colors.dangerSurface,
  },
  rowIcon: {
    width: 36,
    height: 36,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  rowLabel: {
    flex: 1,
  },
  /* Inset to the label, so it separates rows rather than cutting the card. */
  separator: {
    height: 1,
    // Inset past the icon bubble, so it separates rows rather than cutting
    // the card in half.
    marginLeft: spacing.lg + 36 + spacing.md,
    backgroundColor: colors.border,
  },

  footer: {
    alignItems: 'center',
    gap: 2,
    marginTop: spacing.xs,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default AccountScreen;
